import { setCssVariable } from '~/utils/setCssVariable'
import { defu } from 'defu'

// TODO: move these to stylesheets?
// const defaultThemeVars = {
//   oldebroek: {
//     'color-theme-footer-background': '#160849',
//   },
// }

export default function useTheme() {
  const appConfig = useAppConfig()
  const { tenant } = useTenant()
  const { themeConfig } = useThemeConfig()

  // Needs refactoring... this is terrible.
  function updateThemeShapes(shape: 'rounded' | 'straight', appConfig: any) {
    // Temp
    console.log(`[useTheme][updateThemeShapes] setting nui default shapes to`, shape)
    appConfig.theme.defaultShapes.accordion = shape
    appConfig.theme.defaultShapes.autocompleteItem = shape
    // appConfig.theme.defaultShapes.avatar = 'rounded' // avatar always rounded
    appConfig.theme.defaultShapes.button = shape
    appConfig.theme.defaultShapes.buttonAction = shape
    appConfig.theme.defaultShapes.buttonIcon = shape
    appConfig.theme.defaultShapes.buttonClose = shape
    appConfig.theme.defaultShapes.card = shape
    appConfig.theme.defaultShapes.dropdown = shape
    appConfig.theme.defaultShapes.iconBox = shape
    appConfig.theme.defaultShapes.input = shape
    appConfig.theme.defaultShapes.message = shape
    appConfig.theme.defaultShapes.pagination = shape
    appConfig.theme.defaultShapes.progress = shape
    appConfig.theme.defaultShapes.prose = shape
    appConfig.theme.defaultShapes.tabSlider = shape
    updateAppConfig(appConfig)
  }

  // Set css variables and nui props based on the theme config
  function applyThemeConfig(themeConfig: ThemeConfig) {
    if (!themeConfig) {
      console.error(`[useTheme] 🎨 No theme config found`)
      return
    }
    // console.log(`[useTheme] 🎨 Applying theme config`, themeConfig)

    updateThemeShapes(themeConfig.defaultShape as 'rounded' | 'straight', appConfig)

    // Update the font families
    console.log(`[useTheme] 🎨 Setting font families`, themeConfig.fonts)
    if (themeConfig.fonts?.sans) setCssVariable('font-sans', themeConfig.fonts.sans)
    if (themeConfig.fonts?.heading) setCssVariable('font-heading', themeConfig.fonts.heading)

    // Merge with defu so that we fall back to the app config if the theme config is missing a value
    appConfig.theme = defu(themeConfig, appConfig.theme)

    // Update the css variables
    Object.entries(themeConfig.cssVariables).forEach(([name, value]) => {
      if (name && value) {
        console.log(`[useTheme] 🎨 Setting css variable`, name, value)
        setCssVariable(name, value as string)
      }
    })

    updateAppConfig(appConfig)
  }

  // nui default shapes
  const defaultShapes = computed(() => appConfig.theme.defaultShapes)

  // Put these here so it applies across all layouts, including the empty one
  const colorConfig = useColorConfig()

  function updateColors(config: ReturnType<typeof useColorConfig>) {
    // Set the primary color by either using custom color shades or the preset
    if (config.primaryColorShades.value) applyCustomColorShades(config.primaryColorShades.value, 'primary')
    else if (config.primaryColorPreset.value) switchColorPreset('primary', config.primaryColorPreset.value)

    // Set the secondary color by either using custom color shades or the preset
    if (config.secondaryColorShades.value) applyCustomColorShades(config.secondaryColorShades.value, 'secondary')
    // Fallback to primary as the secondary color
    else if (config.secondaryColorPreset.value) switchColorPreset('secondary', config.secondaryColorPreset.value || config.primaryColorPreset.value)
    // Otherwise use a muted color
    else switchColorPreset('secondary', config.mutedColorPreset.value || 'slate')

    // Set the muted color
    switchColorPreset('muted', config.mutedColorPreset.value || 'slate')

    // Set the background color
    // Fallback to the muted-50 color
    setCssVariable('color-theme-background', config.backgroundColor.value || 'rgb(var(--color-muted-50))')
    setCssVariable('color-theme-background-dark', 'rgb(var(--color-muted-950))')

    // Base border color
    setCssVariable('color-theme-border', 'rgb(var(--color-secondary-100))')
    // Header border is the same as base by default
    setCssVariable('color-theme-header-border', 'rgb(var(--color-secondary-100))')

    // Volunteerly uses the primary color for the tabs menu (the default)
    // Mas4you and Roermond use the secondary color for the tabs menu
    const useSecondaryColorForTabsMenu = tenant.value == 'mas4you'
    // Stageplein uses muted color for the tabs menu
    const useMutedColorForTabsMenu = useAppConfig().app?.isStagedoos || tenant.value == 'public'

    if (useSecondaryColorForTabsMenu) {
      // Basically Mas4you theme.
      // Menu border color
      setCssVariable('color-theme-header-border', 'rgb(var(--color-secondary-100))')
      // Menu tabs border color
      setCssVariable('color-theme-menu-tabs-border', 'rgb(var(--color-secondary-200))')
      // Tabs background color, slightly darker than the main background
      setCssVariable('color-theme-menu-tabs-background', 'rgb(var(--color-secondary-100)/.5)')
    }
    else if (useMutedColorForTabsMenu) {
      // Stageplein theme
      // Menu border color
      setCssVariable('color-theme-header-border', 'rgb(var(--color-muted-300))')
      // Menu tabs border color
      setCssVariable('color-theme-menu-tabs-border', 'rgb(var(--color-muted-300))')
      // Tabs background color, slightly darker than the main background
      setCssVariable('color-theme-menu-tabs-background', 'rgb(var(--color-muted-100)/.5)')
    }
    else {
      // Volunteerly theme
      // Menu border color
      setCssVariable('color-theme-header-border', 'rgb(var(--color-primary-200)/.8)')
      // Menu tabs border color
      setCssVariable('color-theme-menu-tabs-border', 'rgb(var(--color-primary-200))')
      // Menu tabs background color, slightly darker than the main background
      setCssVariable('color-theme-menu-tabs-background', 'rgb(var(--color-primary-100)/.5)')
    }

    // Set the default text color for content blocks
    // @maud @roos does this make sense?
    setCssVariable('color-theme-content-text', colorConfig?.textColor?.value || 'rgb(var(--color-primary-950))')
  }

  // TODO: find a better way to do this (see NuxtUI test project)
  // onBeforeMount(() => {
  //   updateColors(colorConfig)
  //   // Apply the theme config after setting colors because we may be overriding some
  //   applyThemeConfig(themeConfig.value)
  // })

  // watch([themeConfig.value], () => {
  //   console.log('[useTheme] 👀 watched themeConfig', themeConfig.value)
  //   updateColors(colorConfig)
  //   applyThemeConfig(themeConfig.value)
  // })

  // watch([colorConfig.primaryColorPreset, colorConfig.secondaryColorPreset, colorConfig.mutedColorPreset, colorConfig.backgroundColor], () => {
  //   updateColors(colorConfig)
  // })

  function initTheme() {
    updateColors(colorConfig)
    applyThemeConfig(themeConfig.value)
  }

  // TODO:
  // - fix dark mode with custom theme bg color (it keeps showing light mode currently)
  // was bg-secondary-50
  const backgroundColorClass = 'bg-theme-background dark:bg-theme-background-dark'

  // TODO: provide can only be used inside setup
  // The default "inner content block" padding that is used in several content blocks, like TextWithUsps block.
  // provide('contentBlockPadding', 'p-8 md:p-10')
  // The spacing between content blocks
  // const contentBlockSpacing = 'space-y-6'
  // provide('contentBlockSpacing', contentBlockSpacing)
  // The default size for the content block title
  // provide('contentBlockTitleSize', '4xl')

  return {
    backgroundColorClass,
    // contentBlockSpacing,
    defaultShapes,
    applyThemeConfig,
    initTheme,
  }
}
